/**
 * Client SPA Entry — 浏览器端入口
 *
 * 职责：
 * 1. 加载所有客户端 Mod（ui-registry + client-runtime + 业务 Mod）
 * 2. 检查 globalThis.__SSR_config 判断是否需要 SSR hydration
 * 3. 如果是 SSR 场景 → hydrateRoot（激活已渲染的 HTML）
 * 4. 如果不是 SSR 场景 → createRoot + render
 *
 * 使用方式（浏览器端 inline script 或模块引入）：
 *   import { bootstrap } from '@hvsin/client-bundle'
 *   bootstrap()
 *
 * 环境参数：
 *   globalThis.__SSR_config = {
 *     enabled: true,
 *     initialState: {...},
 *     loaderData: {...}  // SSR 预取的 loader 数据
 *   }
 *   当 __SSR_config.enabled === true 时执行 hydration，否则执行纯 CSR
 */

import { createRoot, hydrateRoot } from 'react-dom/client'
import { initBrowserContainer } from './browser-init-container.js'
import { ClientRuntime } from '@hvsin/mod-client-runtime'
import type { ModContainer } from 'hvsin'

/**
 * SSR 配置类型（与服务端注入的 __SSR_config 一致）
 */
interface SSRConfig {
  /** 是否启用 SSR hydration */
  enabled: boolean
  /** 当前请求的 URL */
  url?: string
  /** 服务端注入的初始状态 */
  initialState?: Record<string, unknown>
  /** SSR 预取的 loader 数据（避免客户端重复请求） */
  loaderData?: Record<string, any>
}

/**
 * 全局类型声明
 */
declare global {
  // eslint-disable-next-line no-var
  var __SSR_config: SSRConfig | undefined
}

/**
 * 客户端启动入口
 *
 * - 加载 Mod → 获取 ClientRuntime → 判断 SSR hydration 或纯 CSR
 */
export async function bootstrap(): Promise<void> {
  console.log('[Hydration] ====== bootstrap start ======')
  console.log('[Hydration] timestamp:', new Date().toISOString())

  // 0. 检查 SSR 配置
  const ssrConfig = globalThis.__SSR_config
  console.log('[Hydration] __SSR_config:', ssrConfig ? {
    enabled: ssrConfig.enabled,
    url: ssrConfig.url,
    hasLoaderData: !!ssrConfig.loaderData,
    loaderDataKeys: ssrConfig.loaderData ? Object.keys(ssrConfig.loaderData) : [],
  } : 'NOT FOUND')

  // 检查 React Router 的 hydration 数据
  const routerHydrationData = (window as any).__staticRouterHydrationData
  console.log('[Hydration] __staticRouterHydrationData:', routerHydrationData ? 'EXISTS' : 'NOT FOUND')
  if (routerHydrationData) {
    // __staticRouterHydrationData 可能是对象或 JSON 字符串，兼容处理
    const parsed = typeof routerHydrationData === 'string'
      ? JSON.parse(routerHydrationData)
      : routerHydrationData
    console.log('[Hydration] router hydration data keys:', Object.keys(parsed))
  }

  // 1. 初始化容器和所有 Mod
  console.log('[Hydration] Step 1: Loading mods...')
  const container: ModContainer = await initBrowserContainer()
  console.log('[Hydration] Step 1: Done ✓')

  // 2. 从容器中获取 ClientRuntime 实例
  console.log('[Hydration] Step 2: Getting ClientRuntime...')
  const clientRuntime = container.getService<ClientRuntime>('client-runtime', 'clientRuntime')
  console.log('[Hydration] Step 2: Done ✓')

  // 3. 构建 App React 元素（Provider 树 + RouterComponent）
  console.log('[Hydration] Step 3: Creating App element...')
  const appElement = clientRuntime.createAppElement()
  console.log('[Hydration] Step 3: Done ✓')

  // 4. 挂载到 DOM
  console.log('[Hydration] Step 4: Finding #app element...')
  const rootElement = document.getElementById('app')
  if (!rootElement) {
    console.error('[Hydration] #app element NOT FOUND!')
    return
  }
  console.log('[Hydration] Step 4: Done ✓, #app element found')
  console.log('[Hydration] #app innerHTML length:', rootElement.innerHTML.length)
  console.log('[Hydration] #app first child tag:', rootElement.firstElementChild?.tagName)

  // 5. 判断是否需要 SSR hydration
  if (ssrConfig?.enabled) {
    console.log('[Hydration] ====== SSR HYDRATION MODE ======')
    console.log('[Hydration] Calling hydrateRoot()...')
    hydrateRoot(rootElement, appElement)
    console.log('[Hydration] hydrateRoot() returned')
    console.log('[Hydration] ====== HYDRATION COMPLETE ======')

    // 清理 SSR 配置，避免后续客户端导航使用过时的预取数据
    cleanupSSRConfig()
  } else {
    // 纯 CSR 模式：直接在 #app 容器中渲染
    console.log('[Hydration] ====== CSR MODE ======')
    const root = createRoot(rootElement)
    root.render(appElement)
    console.log('[Hydration] Initial render done')
  }

  // 6. 注册 Service Worker（PWA 离线支持 + 安装能力）
  registerServiceWorker()
}

/**
 * 清理 SSR 配置
 *
 * hydration 完成后调用，确保后续客户端导航（Link 点击等）
 * 执行 loader 获取最新数据，而不是使用 SSR 预取的旧数据。
 */
function cleanupSSRConfig(): void {
  // 延迟清理，确保 hydration 过程中的 loader 检查能正常工作
  // 使用 setTimeout 让当前微任务队列中的 loader 检查完成
  setTimeout(() => {
    globalThis.__SSR_config = undefined
    console.log('[ClientBundle] SSR config cleaned up after hydration')
  }, 0)
}

/**
 * 注册 Service Worker（PWA 支持）
 *
 * 在页面加载完成后异步注册 sw.js，提供：
 * - 离线缓存
 * - 独立窗口显示（display: standalone）支持
 * - 添加到主屏幕能力
 */
function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) {
    console.log('[PWA] Service Worker not supported')
    return
  }

  // 延迟注册，避免影响首屏性能
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        console.log('[PWA] Service Worker registered, scope:', registration.scope)

        // 监听新版本
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'activated') {
                console.log('[PWA] New Service Worker activated')
              }
            })
          }
        })
      })
      .catch((error) => {
        console.error('[PWA] Service Worker registration failed:', error)
      })
  })
}

// 浏览器端模块加载时自动执行 bootstrap
// 当此脚本被 <script type="module" src="/assets/spa-entry.js"> 引入时会立即执行
bootstrap()

// 导出类型
export type { SSRConfig }
